import React from 'react';
import { Timeline, Typography } from 'antd';
import type { StatusHistoryItem, QuoteStatus } from '../types';

const { Text } = Typography;

const STATUS_LABELS: Record<QuoteStatus, string> = {
  0: 'Draft',
  1: 'Sent',
  2: 'Accepted',
  3: 'Rejected',
  4: 'Revision Requested',
  5: 'Expired',
  6: 'Cancelled',
};

const STATUS_COLORS: Record<QuoteStatus, string> = {
  0: 'gray',
  1: 'blue',
  2: 'green',
  3: 'red',
  4: 'orange',
  5: 'volcano',
  6: 'gray',
};

function formatDate(s: string): string {
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}/${m}/${day}`;
  } catch {
    return s;
  }
}

function formatDateTimeFull(s: string): string {
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = d.getHours() % 12 || 12;
    const min = String(d.getMinutes()).padStart(2, '0');
    const ampm = d.getHours() < 12 ? 'AM' : 'PM';
    return `${y}/${m}/${day} ${String(h).padStart(2, '0')}:${min} ${ampm}`;
  } catch {
    return s;
  }
}

function formatStatusLabel(status: QuoteStatus | null): string {
  if (status === null) return '(none)';
  return STATUS_LABELS[status] ?? `Unknown (${status})`;
}

function relativeTime(s: string): string {
  const now = Date.now();
  const then = new Date(s).getTime();
  const diffMs = now - then;
  if (Number.isNaN(diffMs)) return '';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(s);
}

export interface StatusHistoryTimelineProps {
  items: StatusHistoryItem[];
}

export const StatusHistoryTimeline: React.FC<StatusHistoryTimelineProps> = ({ items }) => {
  if (items.length === 0) {
    return <Text type="secondary">No status history available.</Text>;
  }

  return (
    <Timeline
      items={items.map((item) => ({
        color: STATUS_COLORS[item.newStatus] ?? 'gray',
        children: (
          <div>
            <div>
              <Text strong>{formatStatusLabel(item.oldStatus)}</Text>
              {' → '}
              <Text strong>{formatStatusLabel(item.newStatus)}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {item.changedByDisplayName ?? 'Unknown'} &middot;{' '}
                <span title={formatDateTimeFull(item.changedAtUtc)}>
                  {relativeTime(item.changedAtUtc)}
                </span>
              </Text>
            </div>
            {item.note && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                  {item.note}
                </Text>
              </div>
            )}
          </div>
        ),
      }))}
    />
  );
};
